import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DatosPersonalesApiDto, DireccionPerfilApiDto } from '../contracts/backend-api.contracts';
import { PERFILES_PERSONALES_MOCK, PerfilMockKey } from '../mocks/perfil.mock';
import {
  BloquePerfil,
  DatosPersonales,
  datosPersonalesVacios,
  DireccionPerfil,
  direccionPerfilVacia,
  SiNoPerfil,
} from '../models/perfil.models';

@Injectable({ providedIn: 'root' })
export class PerfilService {
  private perfilesMock: Record<PerfilMockKey, DatosPersonales> = {
    camilo: { ...PERFILES_PERSONALES_MOCK.camilo, direcciones: PERFILES_PERSONALES_MOCK.camilo.direcciones.map((d) => ({ ...d })) },
    carmen: { ...PERFILES_PERSONALES_MOCK.carmen, direcciones: PERFILES_PERSONALES_MOCK.carmen.direcciones.map((d) => ({ ...d })) },
    damian: { ...PERFILES_PERSONALES_MOCK.damian, direcciones: PERFILES_PERSONALES_MOCK.damian.direcciones.map((d) => ({ ...d })) },
  };
  private readonly baseUrl = `${environment.apiBaseUrl}/perfil/datos-personales`;

  constructor(private readonly http: HttpClient) {}

  getDatosPersonales(perfilMock?: string | null): Observable<DatosPersonales> {
    if (!environment.useMockApi) {
      return this.http.get<DatosPersonalesApiDto>(this.baseUrl).pipe(map((dto) => this.fromApiDto(dto)));
    }

    const perfil = this.perfilMockActivo(perfilMock);
    return of(this.clonar(this.perfilesMock[perfil]));
  }

  /**
   * PUT parcial de Mi perfil.
   * El cliente envía solo las claves del bloque (Guardar y continuar) o el objeto
   * completo (Guardar y finalizar). El mock/backend fusiona con lo ya persistido;
   * las claves omitidas no se borren. Las reglas de * las aplica el front: continuar
   * no exige completar el formulario.
   * TODO(backend): el API real debe hacer merge JSON (semántica PATCH sobre PUT) y
   * no devolver 422 por campos vacíos de otros bloques.
   */
  guardarDatosPersonales(payload: Partial<DatosPersonales>, perfilMock?: string | null): Observable<DatosPersonales> {
    const perfil = this.perfilMockActivo(perfilMock);
    const fusionado = this.fusionar(this.perfilesMock[perfil], payload);

    if (!environment.useMockApi) {
      const body = this.toPartialApiDto(payload);
      return this.http.put<DatosPersonalesApiDto>(this.baseUrl, body).pipe(map((dto) => this.fromApiDto(dto)));
    }

    this.perfilesMock[perfil] = fusionado;
    return of(this.clonar(fusionado));
  }

  private fusionar(actual: DatosPersonales, parcial: Partial<DatosPersonales>): DatosPersonales {
    const siguiente: DatosPersonales = { ...this.clonar(actual), ...parcial };
    if (parcial.direcciones) {
      siguiente.direcciones = parcial.direcciones.map((item) => ({ ...direccionPerfilVacia(), ...item }));
    }
    this.sincronizarLegadoDireccion(siguiente);
    if (parcial.generoRegistral !== undefined) {
      siguiente.sexoRegistral = parcial.generoRegistral;
    } else if (parcial.sexoRegistral !== undefined) {
      siguiente.generoRegistral = parcial.sexoRegistral;
    }
    if (parcial.puebloOriginario !== undefined) {
      siguiente.pueblosOriginarios = parcial.puebloOriginario === 'si' ? 'Si' : parcial.puebloOriginario === 'no' ? 'No' : '';
    }
    if (siguiente.puebloOriginario !== 'si') {
      siguiente.puebloAlQuePertenece = '';
    } else if (siguiente.puebloAlQuePertenece) {
      siguiente.pueblosOriginarios = siguiente.puebloAlQuePertenece;
    }
    if (parcial.discapacidadSiNo !== undefined) {
      siguiente.discapacidad = parcial.discapacidadSiNo === 'si' ? 'Si' : parcial.discapacidadSiNo === 'no' ? 'No' : '';
    }
    if (siguiente.tieneHijos !== 'si') {
      siguiente.cantidadHijos = null;
    }
    return siguiente;
  }

  private sincronizarLegadoDireccion(model: DatosPersonales): void {
    const principal = model.direcciones[0] ?? direccionPerfilVacia();
    model.region = principal.region;
    model.ciudad = principal.comuna;
    model.pais = principal.paisResidencia;
  }

  private fromApiDto(dto: DatosPersonalesApiDto): DatosPersonales {
    const base = datosPersonalesVacios();
    const nombreCompleto = dto.nombre_completo?.trim() ?? '';
    const [primerNombre = '', segundoNombre = '', primerApellido = '', segundoApellido = ''] = nombreCompleto.split(' ');
    const regionCiudadPais = dto.region_ciudad_pais?.trim() ?? '';
    const [region = '', ciudad = '', pais = ''] = regionCiudadPais.split(',').map((item) => item.trim());
    const generoRegistral = dto.genero_registral ?? dto.sexo_registral ?? dto.sexo_genero ?? '';

    const model: DatosPersonales = {
      ...base,
      primerNombre: dto.primer_nombre ?? primerNombre,
      segundoNombre: dto.segundo_nombre ?? segundoNombre,
      primerApellido: dto.primer_apellido ?? primerApellido,
      segundoApellido: dto.segundo_apellido ?? segundoApellido,
      runPasaporte: dto.run_pasaporte ?? '',
      runBloqueado: dto.run_bloqueado ?? Boolean(dto.run_pasaporte?.trim()),
      nacionalidad: dto.nacionalidad ?? '',
      fechaNacimiento: dto.fecha_nacimiento ?? '',
      sexoRegistral: generoRegistral,
      generoRegistral,
      generoDeclarativo: dto.genero_declarativo ?? '',
      nombreSocialCompleto: dto.nombre_social_completo ?? '',
      correoPrincipal: dto.correo_principal ?? '',
      correoAlternativo: dto.correo_alternativo ?? '',
      telefono: dto.telefono ?? '',
      direcciones: this.direccionesDesdeDto(dto, region || dto.region, ciudad || dto.ciudad, pais || dto.pais),
      region: dto.region ?? region,
      ciudad: dto.ciudad ?? ciudad,
      pais: dto.pais ?? pais,
      institucionActual: dto.institucion_actual ?? '',
      cargoRol: dto.cargo_rol ?? '',
      departamentoUnidad: dto.departamento_unidad ?? '',
      tipoVinculo: dto.tipo_vinculo ?? '',
      linkedinAcademico: dto.linkedin_academico ?? '',
      discapacidad: dto.discapacidad ?? '',
      discapacidadSiNo: this.comoSiNo(dto.discapacidad_si_no ?? dto.discapacidad),
      pueblosOriginarios: dto.pueblos_originarios ?? '',
      puebloOriginario: this.comoSiNo(dto.pueblo_originario ?? dto.pueblos_originarios),
      puebloAlQuePertenece: dto.pueblo_al_que_pertenece ?? '',
      tieneHijos: this.comoSiNo(dto.tiene_hijos),
      cantidadHijos: this.comoCantidadHijos(dto.cantidad_hijos),
      conyugeConviviente: dto.conyuge_conviviente ?? '',
      ultimoBloque: this.comoBloque(dto.ultimo_bloque),
    };
    this.sincronizarLegadoDireccion(model);
    if (model.puebloAlQuePertenece) {
      model.puebloOriginario = 'si';
    }
    return model;
  }

  private toPartialApiDto(model: Partial<DatosPersonales>): DatosPersonalesApiDto {
    const dto: DatosPersonalesApiDto = {};
    const set = <K extends keyof DatosPersonalesApiDto>(clave: K, valor: DatosPersonalesApiDto[K], presente: boolean) => {
      if (presente) dto[clave] = valor;
    };

    set('primer_nombre', model.primerNombre, model.primerNombre !== undefined);
    set('segundo_nombre', model.segundoNombre, model.segundoNombre !== undefined);
    set('primer_apellido', model.primerApellido, model.primerApellido !== undefined);
    set('segundo_apellido', model.segundoApellido, model.segundoApellido !== undefined);
    if (model.primerNombre !== undefined || model.primerApellido !== undefined) {
      dto.nombre_completo = `${model.primerNombre ?? ''} ${model.segundoNombre ?? ''} ${model.primerApellido ?? ''} ${model.segundoApellido ?? ''}`.trim();
    }
    set('run_pasaporte', model.runPasaporte, model.runPasaporte !== undefined);
    set('run_bloqueado', model.runBloqueado, model.runBloqueado !== undefined);
    set('nacionalidad', model.nacionalidad, model.nacionalidad !== undefined);
    set('fecha_nacimiento', model.fechaNacimiento, model.fechaNacimiento !== undefined);
    set('genero_registral', model.generoRegistral ?? model.sexoRegistral, model.generoRegistral !== undefined || model.sexoRegistral !== undefined);
    set('sexo_registral', model.generoRegistral ?? model.sexoRegistral, model.generoRegistral !== undefined || model.sexoRegistral !== undefined);
    set('sexo_genero', model.generoRegistral ?? model.sexoRegistral, model.generoRegistral !== undefined || model.sexoRegistral !== undefined);
    set('genero_declarativo', model.generoDeclarativo, model.generoDeclarativo !== undefined);
    set('nombre_social_completo', model.nombreSocialCompleto, model.nombreSocialCompleto !== undefined);
    set('correo_principal', model.correoPrincipal, model.correoPrincipal !== undefined);
    set('correo_alternativo', model.correoAlternativo, model.correoAlternativo !== undefined);
    set('telefono', model.telefono, model.telefono !== undefined);
    if (model.direcciones !== undefined) {
      dto.direcciones = model.direcciones.map((item) => this.direccionToDto(item));
      const principal = model.direcciones[0];
      if (principal) {
        dto.region = principal.region;
        dto.ciudad = principal.comuna;
        dto.pais = principal.paisResidencia;
        dto.region_ciudad_pais = `${principal.region}, ${principal.comuna}, ${principal.paisResidencia}`.replace(/^,\s*|,\s*$/g, '');
      }
    }
    set('discapacidad_si_no', model.discapacidadSiNo, model.discapacidadSiNo !== undefined);
    set('discapacidad', model.discapacidadSiNo, model.discapacidadSiNo !== undefined);
    set('pueblo_originario', model.puebloOriginario, model.puebloOriginario !== undefined);
    set('pueblos_originarios', model.puebloAlQuePertenece || model.puebloOriginario, model.puebloOriginario !== undefined || model.puebloAlQuePertenece !== undefined);
    set('pueblo_al_que_pertenece', model.puebloAlQuePertenece, model.puebloAlQuePertenece !== undefined);
    set('institucion_actual', model.institucionActual, model.institucionActual !== undefined);
    set('cargo_rol', model.cargoRol, model.cargoRol !== undefined);
    set('departamento_unidad', model.departamentoUnidad, model.departamentoUnidad !== undefined);
    set('tipo_vinculo', model.tipoVinculo, model.tipoVinculo !== undefined);
    set('linkedin_academico', model.linkedinAcademico, model.linkedinAcademico !== undefined);
    set('tiene_hijos', model.tieneHijos, model.tieneHijos !== undefined);
    set('cantidad_hijos', model.cantidadHijos, model.cantidadHijos !== undefined);
    set('conyuge_conviviente', model.conyugeConviviente, model.conyugeConviviente !== undefined);
    set('ultimo_bloque', model.ultimoBloque, model.ultimoBloque !== undefined);
    return dto;
  }

  private direccionesDesdeDto(
    dto: DatosPersonalesApiDto,
    region?: string,
    ciudad?: string,
    pais?: string
  ): DireccionPerfil[] {
    if (dto.direcciones?.length) {
      return dto.direcciones.map((item) => ({
        ...direccionPerfilVacia(),
        tipo: item.tipo ?? '',
        paisResidencia: item.pais_residencia ?? '',
        region: item.region ?? '',
        comuna: item.comuna ?? '',
        calleNumeroDepto: item.calle_numero_depto ?? '',
        referenciaAdicional: item.referencia_adicional ?? '',
      }));
    }

    return [
      {
        ...direccionPerfilVacia(),
        paisResidencia: pais ?? '',
        region: region ?? '',
        comuna: ciudad ?? '',
      },
    ];
  }

  private direccionToDto(item: DireccionPerfil): DireccionPerfilApiDto {
    return {
      tipo: item.tipo,
      pais_residencia: item.paisResidencia,
      region: item.region,
      comuna: item.comuna,
      calle_numero_depto: item.calleNumeroDepto,
      referencia_adicional: item.referenciaAdicional,
    };
  }

  private comoSiNo(valor?: string): SiNoPerfil {
    const normalizado = (valor ?? '').trim().toLowerCase();
    if (normalizado === 'si' || normalizado === 'sí') return 'si';
    if (normalizado === 'no') return 'no';
    return '';
  }

  private comoCantidadHijos(valor?: number | null): number | null {
    if (valor == null || Number.isNaN(Number(valor))) return null;
    const n = Math.round(Number(valor));
    if (n < 1 || n > 10) return null;
    return n;
  }

  private comoBloque(valor?: string): BloquePerfil {
    if (valor === 'contacto' || valor === 'diversidad' || valor === 'familia' || valor === 'identidad') {
      return valor;
    }
    return 'identidad';
  }

  private clonar(model: DatosPersonales): DatosPersonales {
    return {
      ...model,
      direcciones: model.direcciones.map((item) => ({ ...item })),
    };
  }

  private perfilMockActivo(perfilMock?: string | null): PerfilMockKey {
    const perfilParam = (perfilMock ?? this.obtenerPerfilDesdeUrl()).trim().toLowerCase();

    if (this.esPerfilMockKey(perfilParam)) {
      return perfilParam;
    }

    return 'camilo';
  }

  private obtenerPerfilDesdeUrl(): string {
    if (typeof globalThis.location === 'undefined') return '';

    try {
      return (new URLSearchParams(globalThis.location.search).get('perfil') ?? '').trim().toLowerCase();
    } catch {
      return '';
    }
  }

  private esPerfilMockKey(valor: string): valor is PerfilMockKey {
    return Object.prototype.hasOwnProperty.call(PERFILES_PERSONALES_MOCK, valor);
  }
}
